import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ticketsApi, customersApi } from '../lib/api-hooks';
import { formatSakNr, formatRelative } from '../lib/format';
import { StatusBadge } from '../components/ticket/StatusBadge';

export default function SearchPage() {
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const { data: ticketResults } = useQuery({
    queryKey: ['search-tickets', query],
    queryFn: () => ticketsApi.list({ q: query }),
    enabled: query.length >= 2,
  });

  const { data: customerResults } = useQuery({
    queryKey: ['search-customers', query],
    queryFn: () => customersApi.list(query),
    enabled: query.length >= 2,
  });

  // Ctrl+K focus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const input = document.getElementById('global-search') as HTMLInputElement;
        input?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <div className="mx-auto max-w-3xl px-4 py-6 md:px-6">
      <h1 className="mb-4 text-xl font-semibold text-slate-800">Søk</h1>

      <div className="relative mb-6">
        <input
          id="global-search"
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Søk i saker, kunder… (Ctrl+K)"
          className="input pl-10 text-base"
          autoFocus
        />
        <svg
          className="absolute left-3 top-3 h-5 w-5 text-slate-400"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <path d="m21 21-4.3-4.3" />
        </svg>
      </div>

      {query.length < 2 ? (
        <div className="text-center text-sm text-slate-400">
          Skriv minst 2 tegn for å søke.
        </div>
      ) : (
        <div className="space-y-6">
          {/* Saker */}
          {ticketResults && ticketResults.tickets.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-medium text-slate-600">
                Saker ({ticketResults.total})
              </h2>
              <div className="card divide-y divide-slate-100">
                {ticketResults.tickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => navigate(`/ticket/${t.id}`)}
                    className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-slate-400">{formatSakNr(t.sakNr)}</span>
                        <StatusBadge status={t.status} />
                      </div>
                      <p className="truncate text-sm font-medium text-slate-800">{t.tittel}</p>
                      {t.customer && (
                        <p className="truncate text-xs text-slate-500">{t.customer.epost}</p>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">{formatRelative(t.opprettet)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Kunder */}
          {customerResults && customerResults.length > 0 && (
            <div>
              <h2 className="mb-2 text-sm font-medium text-slate-600">
                Kunder ({customerResults.length})
              </h2>
              <div className="card divide-y divide-slate-100">
                {customerResults.map((c) => (
                  <div key={c.id} className="px-4 py-3">
                    <p className="text-sm font-medium text-slate-800">{c.navn || c.epost}</p>
                    {c.navn && <p className="text-xs text-slate-500">{c.epost}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {ticketResults?.tickets.length === 0 && customerResults?.length === 0 && (
            <div className="text-center text-sm text-slate-400">
              Ingen resultater for «{query}».
            </div>
          )}
        </div>
      )}
    </div>
  );
}
